
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { supabase } from "@/lib/supabase";

export default function Experience() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState("next");
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const loadExperiences = async () => {
            const { data, error } = await supabase
                .from("experience_page_entries")
                .select("*")
                .order("sort_order", { ascending: true })
                .order("created_at", { ascending: true });

            if (error) {
                console.error("Could not load Experience entries:", error);
            } else {
                setEntries(data || []);
            }

            setLoading(false);
        };

        loadExperiences();
    }, []);

    const navigateExperience = (direction) => {
        if (entries.length <= 1) return;

        setSlideDirection(direction);

        setActiveIndex((current) =>
            direction === "next"
                ? (current + 1) % entries.length
                : (current - 1 + entries.length) % entries.length
        );

        setAnimationKey((current) => current + 1);
    };

    const activeExperience = entries[activeIndex];

    const activeStats = activeExperience
        ? Array.isArray(activeExperience.stats)
            ? activeExperience.stats
            : []
        : [];

    return (
        <>
            <style>{`
                @keyframes experienceSlideNext {
                    from {
                        opacity: 0;
                        transform: translateX(45px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes experienceSlidePrev {
                    from {
                        opacity: 0;
                        transform: translateX(-45px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .experience-slide-next {
                    animation: experienceSlideNext 450ms ease both;
                }

                .experience-slide-prev {
                    animation: experienceSlidePrev 450ms ease both;
                }

                @media (prefers-reduced-motion: reduce) {
                    .experience-slide-next,
                    .experience-slide-prev {
                        animation: none;
                    }
                }
            `}</style>

            <PageHeader
                crumb="HOME / EXPERIENCE"
                eyebrow="03 — WHERE I'VE BEEN"
                lines={["EXPERIENCE"]}
                sub="Learning by doing — out on the sales floor."
            />

            <section className="px-5 lg:px-12 xl:px-16 py-14 lg:py-20 pb-24">
                {loading ? (
                    <p className="mono text-sm text-muted2">
                        LOADING EXPERIENCE...
                    </p>
                ) : entries.length === 0 ? (
                    <p
                        className="mono text-sm text-muted2"
                        data-testid="experience-empty-state"
                    >
                        No experience entries yet — coming soon.
                    </p>
                ) : (
                    <>
                        <div
                            key={animationKey}
                            className={
                                slideDirection === "next"
                                    ? "experience-slide-next"
                                    : "experience-slide-prev"
                            }
                        >
                            <Reveal>
                                <div
                                    className="grid grid-cols-1 sm:grid-cols-3 border border-line divide-y sm:divide-y-0 sm:divide-x divide-line bg-paper2"
                                    data-testid="experience-stats"
                                >
                                    {[0, 1, 2].map((index) => {
                                        const stat = activeStats[index] || {
                                            value: "",
                                            label: "",
                                        };

                                        return (
                                            <div
                                                key={`${activeExperience.id}-stat-${index}`}
                                                className="p-8"
                                            >
                                                <p className="font-display text-4xl md:text-5xl tracking-tight">
                                                    {stat.value}
                                                </p>

                                                <p className="mono text-[10px] tracking-[0.2em] text-muted2 mt-3">
                                                    {stat.label}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Reveal>

                            <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.3fr]">
                                <Reveal>
                                    <p className="mono text-[11px] tracking-[0.25em] text-muted2">
                                        {(activeExperience.period || "").toUpperCase()}
                                    </p>

                                    <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-4">
                                        {activeExperience.role}
                                    </h2>

                                    <p className="mt-3 text-lg">
                                        {activeExperience.org}
                                        {activeExperience.location
                                            ? ` — ${activeExperience.location}`
                                            : ""}
                                    </p>
                                </Reveal>

                                <Reveal delay={0.1}>
                                    <ol className="border-t border-line">
                                        {(activeExperience.bullets || []).map(
                                            (bullet, index) => (
                                                <li
                                                    key={index}
                                                    className="flex gap-6 border-b border-line py-6"
                                                >
                                                    <span className="mono text-xs text-muted2 pt-1.5">
                                                        {String(index + 1).padStart(2, "0")}
                                                    </span>

                                                    <p className="text-lg leading-snug">
                                                        {bullet}
                                                    </p>
                                                </li>
                                            )
                                        )}
                                    </ol>
                                </Reveal>
                            </div>
                        </div>

                        <div className="mt-12 pt-6 border-t border-line flex items-center justify-between">
                            <p className="mono text-xs tracking-widest text-muted2">
                                {String(activeIndex + 1).padStart(2, "0")} / {String(entries.length).padStart(2, "0")}
                            </p>

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => navigateExperience("prev")}
                                    disabled={entries.length <= 1}
                                    aria-label="Previous experience"
                                    className="border border-line p-3 hover:bg-paper2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => navigateExperience("next")}
                                    disabled={entries.length <= 1}
                                    aria-label="Next experience"
                                    className="border border-line p-3 hover:bg-paper2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </>
    );
}
